import { ITimesheet } from "@/customTypings/Timesheet";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useEffect, useState } from "react";
import { Alert, Button, Card, Placeholder, Stack } from "react-bootstrap";
import Col from "react-bootstrap/esm/Col";
import Container from "react-bootstrap/esm/Container";
import Row from "react-bootstrap/esm/Row";
import { Helmet } from "react-helmet";
import Skeleton from "react-loading-skeleton";
import { NavLink } from "react-router-dom";
import { AddTimesheetModel } from "../../components/Models/AddTimesheetModel";
import { ConfirmationModel } from "../../components/Models/ConfirmationModel";
import { TimesheetType } from "../../constants/TimesheetType";
import { IJob } from "../../customTypings/Job";
import { jobService } from "../../services";
import { timesheetService } from "../../services/timesheet.service";
import { showToastSuccess, showToastError } from "../../helpers";
dayjs.extend(relativeTime);

const MyTimesheet = () => {
    const [loading, setLoading] = useState(true);
    const [alertText, setAlertText] = useState<string | undefined>(undefined);
    const [activeTimesheet, setActiveTimesheet] = useState<ITimesheet | null>(null);
    const [activeJob, setActiveJob] = useState<IJob | null>(null);
    const [showStartDayConfirm, setShowStartDayConfirm] = useState(false);
    const [showCompleteDayConfirm, setShowCompleteDayConfirm] = useState(false);
    const [showAddTimsheetEntry, setShowAddTimsheetEntry] = useState(false);

    useEffect(() => {
        handleGetTimesheet();
    }, []);

    useEffect(() => {
        if (activeTimesheet?.jobId !== undefined) {
            handleGetJob();
        } else {
            setActiveJob(null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTimesheet?.jobId]);

    const handleGetTimesheet = () => {
        setAlertText(undefined);
        timesheetService.getActive().then(
            (response) => {
                if (response.status !== "Failure") {
                    setActiveTimesheet(response);
                    setLoading(false);
                } else {
                    showToastError(response.message);
                }
            },
            (error: any) => {
                if (error.status === 400 && error.errors) {
                    Object.keys(error.errors).forEach((key) => {
                        const messages = error.errors[key];
                        if (Array.isArray(messages)) {
                            messages.forEach((message) => showToastError(message));
                        }
                    });
                } else {
                    showToastError("An unexpected error occurred!");
                }
            }
        );
    };

    const handleGetJob = () => {
        setAlertText(undefined);
        jobService.get(activeTimesheet?.jobId ?? "").then(
            (response) => {
                if (response.status !== "Failure") {
                    setActiveJob(response);
                    setLoading(false);
                } else {
                    showToastError(response.message);
                }
            },
            (error: any) => {
                if (error.status === 400 && error.errors) {
                    Object.keys(error.errors).forEach((key) => {
                        const messages = error.errors[key];
                        if (Array.isArray(messages)) {
                            messages.forEach((message) => showToastError(message));
                        }
                    });
                } else {
                    showToastError("An unexpected error occurred!");
                }
            }
        );
    };

    const handleCompleteTask = () => {
        setAlertText(undefined);
        timesheetService.complete(activeTimesheet?.id ?? "").then(
            (response) => {
                if (response.status !== "Failure") {
                    handleGetTimesheet();
                    showToastSuccess("Task marked as completed succcessfully");
                } else {
                    showToastError(response.message);
                }
            },
            (error: any) => {
                if (error.status === 400 && error.errors) {
                    Object.keys(error.errors).forEach((key) => {
                        const messages = error.errors[key];
                        if (Array.isArray(messages)) {
                            messages.forEach((message) => showToastError(message));
                        }
                    });
                } else {
                    showToastError("An unexpected error occurred!");
                }
            }
        );
    };

    const handleStartDay = () => {
        setAlertText(undefined);
        const newTimesheet: ITimesheet = {
            type: TimesheetType.Travel,
        };
        timesheetService.add(newTimesheet).then(
            (response) => {
                if (response.status !== "Failure") {
                    handleGetTimesheet();
                } else {
                    showToastError(response.message);
                }
            },
            (error: any) => {
                if (error.status === 400 && error.errors) {
                    Object.keys(error.errors).forEach((key) => {
                        const messages = error.errors[key];
                        if (Array.isArray(messages)) {
                            messages.forEach((message) => showToastError(message));
                        }
                    });
                } else {
                    showToastError("An unexpected error occurred!");
                }
            }
        );
    };

    return (
        <Container fluid>
            <Helmet>
                <title>My Timesheet</title>
            </Helmet>
            <Row>
                <Col>
                    <h1 className="my-3">My Timesheet</h1>
                    <Card>
                        <Card.Body>
                            {loading ? (
                                <>
                                    <Card.Title>Active Task</Card.Title>
                                    <Row>
                                        <Col sm={3}>Type</Col>
                                        <Col>
                                            <Skeleton width="20rem" height="2rem" />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col sm={3}>Start</Col>
                                        <Col>
                                            <Skeleton width="20rem" height="2rem" />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col sm={3}>Started</Col>
                                        <Col>
                                            <Skeleton width="20rem" height="2rem" />
                                        </Col>
                                    </Row>
                                    <Stack gap={3} direction="horizontal">
                                        <Placeholder.Button variant="outline-success" xs={1} className="ms-auto" />
                                        <Placeholder.Button variant="success" xs={1} />
                                    </Stack>
                                </>
                            ) : (
                                <>
                                    {activeTimesheet ? (
                                        <>
                                            <Card.Title>Active Task</Card.Title>
                                            {activeJob && (
                                                <Row>
                                                    <Col sm={3}>Job</Col>
                                                    <Col>
                                                        <NavLink to={"/jobs/" + activeJob.id}>{activeJob.jobNumber}</NavLink>
                                                    </Col>
                                                </Row>
                                            )}
                                            <Row>
                                                <Col sm={3}>Type</Col>
                                                <Col>{activeTimesheet.type ? activeTimesheet.type : "Not set yet"}</Col>
                                            </Row>
                                            <Row>
                                                <Col sm={3}>Start</Col>
                                                <Col>{activeTimesheet.startDate ? dayjs(activeTimesheet.startDate).format("DD-MM-YYYY HH:mm:ss") : "Not set yet"}</Col>
                                            </Row>
                                            <Row>
                                                <Col sm={3}>Started</Col>
                                                <Col>{dayjs(activeTimesheet.startDate).fromNow()}</Col>
                                            </Row>
                                            <Stack gap={3} direction="horizontal">
                                                <Button className="ms-auto" variant="outline-success" onClick={() => setShowCompleteDayConfirm(true)}>
                                                    Complete day
                                                </Button>
                                                <Button variant="success" onClick={() => setShowAddTimsheetEntry(true)}>
                                                    New Task
                                                </Button>
                                            </Stack>
                                        </>
                                    ) : (
                                        <>
                                            <Card.Title>Active Task</Card.Title>
                                            <Row>
                                                <Col sm={3}>Type</Col>
                                                <Col>Not set yet</Col>
                                            </Row>
                                            <Row>
                                                <Col sm={3}>Start</Col>
                                                <Col>Not set yet</Col>
                                            </Row>
                                            <Row>
                                                <Col sm={3}>Started</Col>
                                                <Col></Col>
                                            </Row>
                                            <Stack gap={3} direction="horizontal">
                                                <Button className="ms-auto" variant="success" onClick={() => setShowStartDayConfirm(true)}>
                                                    Start day
                                                </Button>
                                            </Stack>
                                        </>
                                    )}
                                </>
                            )}
                            {alertText && (
                                <Alert variant="danger" className="mt-3">
                                    {alertText}
                                </Alert>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <ConfirmationModel
                show={showStartDayConfirm}
                onClose={(confirmed) => {
                    if (confirmed) {
                        handleStartDay();
                    }
                    setShowStartDayConfirm(false);
                }}
                title="Start Day"
                buttonSuccessText="Start"
                buttenCancelText="Cancel"
            />
            <ConfirmationModel
                show={showCompleteDayConfirm}
                onClose={(confirmed) => {
                    if (confirmed) {
                        handleCompleteTask();
                    }
                    setShowCompleteDayConfirm(false);
                }}
                title="Complete Day"
                buttonSuccessText="Complete"
                buttenCancelText="Cancel"
            />
            <AddTimesheetModel
                show={showAddTimsheetEntry}
                onClose={(added) => {
                    if (added) {
                        handleGetTimesheet();
                    }
                    setShowAddTimsheetEntry(false);
                }}
            />
        </Container>
    );
};

export { MyTimesheet };

